import { ExternalLink, RefreshCw, Star } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "../api";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import { ErrorState, LoadingState } from "./Ui";
import { sentence } from "./Workflow";

type ClientOption = { id: number; name: string; status: string; locations: Array<{ id: number; market_name: string; city: string | null }> };
type BlLocation = {
  bl_location_id: number;
  business_name: string;
  address: string | null;
  city: string | null;
  link: { client_id: number; client_name: string; location_id: number; location_name: string; last_synced_at: string | null; last_status: string | null; last_error: string | null } | null;
  suggested: { client_id: number; score: number } | null;
};
type Part = { status: string; message?: string; url?: string | null };
type Summary = {
  reviews?: Part & { rating?: number | null; reviews_count?: number | null; new_reviews?: number; recommendations?: number; last_update?: string | null };
  rankings?: Part & { keywords?: Array<{ keyword: string; google: number | null; maps: number | null; mobile: number | null; volume?: number | null }>; last_processed_at?: string | null };
  citations?: Part & { report_status?: string | null };
};
export type ClientLink = { bl_location_id: number; business_name: string | null; address: string | null; location_name: string; summary: Summary; last_synced_at: string | null; last_status: string | null; last_error: string | null;
  /** The Rank Tracker run before the current one (keywords lower-cased), when there is one. */
  previous_ranks?: { processed_on: string; keywords: Record<string, { google: number | null; maps: number | null; mobile: number | null }> } | null };
/** GET /api/brightlocal/clients/:id: is a key saved, and the client's linked locations. */
export type BrightLocalClient = { configured: boolean; links: ClientLink[] };

const PART_NOTE: Record<string, string> = {
  not_entitled: "Not on your BrightLocal plan",
  no_report: "No report set up in BrightLocal",
  not_found: "Report not found in BrightLocal",
  rate_limited: "BrightLocal rate limit, try again shortly",
};
const marketKey = (value: string | null | undefined) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
function matchingLocation(loc: BlLocation, client?: ClientOption) {
  if (!client) return "new";
  const city = marketKey(loc.city);
  const business = marketKey(loc.business_name);
  return client.locations.find((candidate) => {
    const market = marketKey(candidate.market_name);
    return (city && (market === city || marketKey(candidate.city) === city)) ||
      (market.length >= 5 && business.endsWith(market));
  })?.id ?? "new";
}

/**
 * Settings → BrightLocal: match each BrightLocal location to a client location.
 * With `clientId` (Client settings) it lists only that client's links and the
 * locations nobody has linked yet, and never offers another client.
 */
export function BrightLocalLinks({ clientId: fixedId, onChanged }: { clientId?: number; onChanged?: () => void } = {}) {
  const request = useApi<{ clients: ClientOption[]; locations: BlLocation[]; key_source?: "client" | "agency" }>(
    fixedId ? `/api/brightlocal/locations?client_id=${fixedId}` : "/api/brightlocal/locations",
  );
  const [choice, setChoice] = useState<Record<number, { client_id?: number; location_id?: number | "new" | "" }>>({});
  const [showOthers, setShowOthers] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ id: number; text: string; tone: "danger" | "success" } | null>(null);

  if (request.loading && !request.data) return <LoadingState label="Loading BrightLocal locations" />;
  if (request.error) return <ErrorState message={request.error} onRetry={request.reload} />;
  const { clients = [], locations: all = [] } = request.data || {};
  const open = fixedId ? all.filter((l) => !l.link || l.link.client_id === fixedId) : all;
  // On a client's page the agency's BrightLocal account also holds other businesses (10-20: a Sacramento
  // dental office was offered to Junk King Sacramento with its location picked, one click from linking).
  // Show this client's links and suggested matches; the rest only on request, and never pre-matched.
  const ours = (l: BlLocation) => !fixedId || l.link?.client_id === fixedId || l.suggested?.client_id === fixedId;
  const others = open.filter((l) => !ours(l));
  const locations = fixedId && !showOthers ? open.filter(ours) : open;
  const linked = locations.filter((l) => l.link).length;

  async function run(id: number, fn: () => Promise<string>) {
    setBusy(id);
    setNotice(null);
    try { setNotice({ id, text: await fn(), tone: "success" }); request.reload(); onChanged?.(); }
    catch (e) { setNotice({ id, text: e instanceof Error ? e.message : "Something went wrong", tone: "danger" }); }
    finally { setBusy(null); }
  }

  return (
    <div className="bl-links">
      <div className="bl-links-head">
        {fixedId ? (
          <>
            <h4>BrightLocal locations</h4>
            <span>{linked} linked, using {request.data?.key_source === "client" ? "this client's key" : "the agency key"}</span>
          </>
        ) : (
          <>
            <h4>Client links</h4>
            <span>{linked} of {locations.length} BrightLocal {locations.length === 1 ? "location" : "locations"} linked</span>
          </>
        )}
      </div>
      {!locations.length ? (
        <p className="integration-detail">{!all.length ? "No locations in this BrightLocal account yet." : others.length ? "No BrightLocal location matches this client yet." : "Every location in this BrightLocal account is linked to another client."}</p>
      ) : (
        <ul className="bl-link-list">
          {locations.map((loc) => {
            const pick = choice[loc.bl_location_id] || {};
            const clientId = fixedId ?? pick.client_id ?? loc.link?.client_id ?? loc.suggested?.client_id;
            const client = clients.find((c) => c.id === clientId);
            const locationId = pick.location_id ?? (loc.link && loc.link.client_id === clientId ? loc.link.location_id : ours(loc) ? matchingLocation(loc, client) : "");
            const dirty = (!loc.link || clientId !== loc.link.client_id || locationId !== loc.link.location_id) && locationId !== "";
            const set = (patch: typeof pick) => setChoice({ ...choice, [loc.bl_location_id]: { ...pick, ...patch } });
            return (
              <li key={loc.bl_location_id}>
                <div className="bl-link-name">
                  <strong>{loc.business_name}</strong>
                  <span>{loc.address || "No address"}</span>
                  {loc.link ? (
                    <span className={`bl-state ${loc.link.last_status === "failed" ? "bad" : "good"}`}>
                      Linked. {loc.link.last_synced_at ? `Synced ${formatDate(loc.link.last_synced_at)}` : "Not synced yet"}{loc.link.last_status === "partial" ? " (some reports unavailable)" : ""}.
                      {loc.link.last_error ? ` ${loc.link.last_error}` : ""}
                    </span>
                  ) : loc.suggested && !pick.client_id && (!fixedId || loc.suggested.client_id === fixedId) ? <span className="bl-state">Suggested match</span> : null}
                </div>
                {!fixedId && (
                  <select aria-label={`Client for ${loc.business_name}`} value={clientId ?? ""} onChange={(e) => set({ client_id: Number(e.target.value) || undefined, location_id: undefined })}>
                    <option value="">Choose client</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.status !== "active" ? ` (${c.status})` : ""}</option>)}
                  </select>
                )}
                <select aria-label={`Location for ${loc.business_name}`} value={String(locationId)} disabled={!client} onChange={(e) => set({ location_id: e.target.value === "new" ? "new" : e.target.value === "" ? "" : Number(e.target.value) })}>
                  {locationId === "" && <option value="">Choose a location</option>}
                  {client?.locations.map((l) => <option key={l.id} value={l.id}>{l.market_name}</option>)}
                  <option value="new">New location: {loc.city || loc.business_name}</option>
                </select>
                <div className="bl-link-actions">
                  <button className="button secondary" disabled={busy !== null || !client || !dirty}
                    onClick={() => run(loc.bl_location_id, async () => {
                      await api(`/api/brightlocal/links/${loc.bl_location_id}`, { method: "PUT", body: JSON.stringify({ client_id: clientId, location_id: locationId }) });
                      setChoice((c) => { const n = { ...c }; delete n[loc.bl_location_id]; return n; });
                      return `Linked to ${client?.name}. Sync to pull its reviews.`;
                    })}>
                    {busy === loc.bl_location_id ? "Saving…" : loc.link ? "Save" : "Link"}
                  </button>
                  {loc.link && (
                    <>
                      <button className="button secondary" disabled={busy !== null}
                        onClick={() => run(loc.bl_location_id, async () => {
                          const r = await api<{ status: string; error: string | null; summary: Summary }>(`/api/brightlocal/links/${loc.bl_location_id}/sync`, { method: "POST" });
                          if (r.status !== "ok") throw new Error(r.error || (r.status === "partial" ? "Some BrightLocal reports could not sync. Check this client's report panel for details." : "BrightLocal is not configured for this client."));
                          return `Synced: ${r.summary.reviews?.new_reviews ?? 0} new reviews.`;
                        })}>
                        <RefreshCw aria-hidden="true" /> Sync
                      </button>
                      <button className="text-button" disabled={busy !== null}
                        onClick={() => { if (window.confirm(`Unlink ${loc.business_name}? Reviews already imported stay.`)) void run(loc.bl_location_id, async () => { await api(`/api/brightlocal/links/${loc.bl_location_id}`, { method: "DELETE" }); return "Unlinked."; }); }}>
                        Unlink
                      </button>
                    </>
                  )}
                </div>
                {notice?.id === loc.bl_location_id && <p className={`inline-notice ${notice.tone}`} role="status">{notice.text}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {fixedId && others.length > 0 && (
        <button type="button" className="text-button" onClick={() => setShowOthers((v) => !v)}>
          {showOthers ? "Show only this client's matches" : `Show other BrightLocal locations (${others.length})`}
        </button>
      )}
      <p className="integration-detail">
        Daily sync runs once "BrightLocal reviews and rankings sync" is switched on in{" "}
        <Link to="/settings#security">Settings, System &amp; security</Link>, Scheduled job ownership.
      </p>
    </div>
  );
}

/** A report link only when it is http(s); BrightLocal's own URLs always are. */
const reportUrl = (value: string | null | undefined) => (value && /^https?:\/\//i.test(value) ? value : null);
// Citation Tracker's own status words, said plainly. Anything new reads as a sentence.
const REPORT_STATUS: Record<string, string> = { ok: "Report ready", complete: "Report ready", running: "Updating in BrightLocal" };
const reportStatus = (value: string | null | undefined) => (value ? REPORT_STATUS[value.toLowerCase()] || sentence(value) : "Report ready");

/**
 * Reviews & citations: what BrightLocal says about this client. The page loads
 * the data (with the rest of the page, so nothing jumps) and draws this only
 * when a location is linked. `imported` is NOLA's own count per location name,
 * set beside BrightLocal's when the two differ.
 */
export function BrightLocalPanel({ clientId, data, imported, onSynced }: {
  clientId: number;
  data: BrightLocalClient;
  imported: Record<string, number>;
  onSynced: () => void;
}) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { links } = data;
  if (!links.length) return null;
  const canSync = data.configured && (user?.role === "superadmin" || user?.role === "exec");

  return (
    <section className="panel bl-panel">
      <div className="panel-heading">
        <div>
          <h2>BrightLocal</h2>
          {links.length > 1 && <p className="panel-note">{links.length} linked locations</p>}
        </div>
        {canSync && (
          <button className="button secondary" disabled={busy} onClick={async () => {
            setBusy(true); setError("");
            try { await api(`/api/brightlocal/clients/${clientId}/sync`, { method: "POST" }); onSynced(); }
            catch (e) { setError(e instanceof Error ? e.message : "Sync failed"); }
            finally { setBusy(false); }
          }}>
            <RefreshCw aria-hidden="true" /> {busy ? "Syncing…" : "Sync now"}
          </button>
        )}
      </div>
      {error && <p className="inline-notice danger" role="alert">{error}</p>}
      {links.map((l) => {
        const s = l.summary || {};
        const kws = s.rankings?.keywords || [];
        const onBrightLocal = s.reviews?.reviews_count ?? null;
        const inNola = imported[l.location_name] ?? 0;
        const reviewsReport = reportUrl(s.reviews?.url);
        const citationsReport = reportUrl(s.citations?.url);
        const ranksReport = reportUrl(s.rankings?.url);
        // One flat row per report: what BrightLocal says, then where to read more.
        return (
          <div key={l.bl_location_id} className="bl-location">
            <p className="bl-location-name">
              <strong>{l.business_name || l.location_name}</strong>
              {l.business_name && l.business_name !== l.location_name && <span>{l.location_name}</span>}
              <span>{l.last_synced_at ? `Synced ${formatDate(l.last_synced_at)}` : "Not synced yet"}</span>
              {l.last_error && <span className="bl-location-error">{l.last_error}</span>}
            </p>
            <dl className="bl-rows">
              <div className="bl-row">
                <dt>Reviews</dt>
                <dd>
                  {s.reviews?.status === "ok" ? (
                    <>
                      {/* One location: its rating and count are the page's own tiles, so they are not repeated here. */}
                      {links.length > 1 && (
                        <span className="bl-rating"><Star aria-hidden="true" />{s.reviews.rating != null ? Number(s.reviews.rating).toFixed(1) : "—"}</span>
                      )}
                      {onBrightLocal != null && inNola < onBrightLocal
                        ? `${onBrightLocal.toLocaleString()} on BrightLocal, ${inNola.toLocaleString()} imported. Reviews without a star rating or date are not imported.`
                        : links.length > 1 ? `${(onBrightLocal ?? 0).toLocaleString()} on BrightLocal, every one imported` : "Every review is imported"}
                    </>
                  ) : <span className="muted">{PART_NOTE[s.reviews?.status || ""] || s.reviews?.message || "Sync to load"}</span>}
                </dd>
                <dd className="bl-row-links">
                  {reviewsReport && <a href={reviewsReport} target="_blank" rel="noreferrer"><ExternalLink aria-hidden="true" />Reputation report</a>}
                </dd>
              </div>
              <div className="bl-row">
                <dt>Citations</dt>
                <dd>{s.citations?.status === "ok" ? reportStatus(s.citations.report_status) : <span className="muted">{PART_NOTE[s.citations?.status || ""] || "Sync to load"}</span>}</dd>
                <dd className="bl-row-links">
                  {s.citations?.status === "ok" && citationsReport && <a href={citationsReport} target="_blank" rel="noreferrer"><ExternalLink aria-hidden="true" />Citation Tracker report</a>}
                </dd>
              </div>
              <div className="bl-row">
                <dt>Keyword rankings</dt>
                <dd>
                  {s.rankings?.status === "ok"
                    ? kws.length ? `${kws.length.toLocaleString()} ${kws.length === 1 ? "keyword" : "keywords"} tracked` : "No keywords tracked"
                    : <span className="muted">{PART_NOTE[s.rankings?.status || ""] || "Sync to load"}</span>}
                </dd>
                <dd className="bl-row-links">
                  {/* The full table lives on Keywords, beside the research it informs. */}
                  {s.rankings?.status === "ok" && kws.length > 0 && <Link to={`/keywords?client_id=${clientId}&tab=rankings`}>View rankings</Link>}
                  {ranksReport && <a href={ranksReport} target="_blank" rel="noreferrer"><ExternalLink aria-hidden="true" />Rank Tracker report</a>}
                </dd>
              </div>
            </dl>
          </div>
        );
      })}
    </section>
  );
}
